import type { SupabaseClient } from "@supabase/supabase-js"

export type ChatParticipantProfile = {
  username: string
  avatar_url?: string | null
}

export function canonicalChatRoomName(roomName: string): string {
  return roomName.includes(":") ? roomName.split(":").sort().join(":") : roomName
}

export function otherParticipantIdFromRoom(roomName: string, currentUserId: string): string | null {
  const canonical = canonicalChatRoomName(roomName)
  return canonical.split(":").filter(Boolean).find((id) => id !== currentUserId) ?? null
}

export function fallbackParticipantUsername(userId: string): string {
  return `user_${userId.replace(/-/g, "").slice(0, 8)}`
}

type ProfileRow = {
  id: string
  username: string
  avatar_url?: string | null
}

export async function fetchChatParticipantProfiles(
  supabase: SupabaseClient,
  currentUserId: string,
  profileIds: string[],
): Promise<Map<string, ChatParticipantProfile>> {
  const uniqueIds = Array.from(new Set(profileIds.filter(Boolean)))
  const map = new Map<string, ChatParticipantProfile>()
  if (uniqueIds.length === 0) return map

  const { data, error } = await supabase.rpc("get_public_profiles_with_followers_by_ids", {
    p_current_user: currentUserId,
    p_profile_ids: uniqueIds,
  })

  if (error || !Array.isArray(data)) return map

  for (const row of data as ProfileRow[]) {
    if (!row?.id) continue
    map.set(row.id, {
      username: row.username?.trim() || fallbackParticipantUsername(row.id),
      avatar_url: row.avatar_url ?? null,
    })
  }

  return map
}

export async function fetchChatOtherParticipantProfile(
  supabase: SupabaseClient,
  currentUserId: string,
  roomName: string,
): Promise<ChatParticipantProfile | null> {
  const otherId = otherParticipantIdFromRoom(roomName, currentUserId)
  if (!otherId) return null

  const profiles = await fetchChatParticipantProfiles(supabase, currentUserId, [otherId])
  const profile = profiles.get(otherId)
  if (profile) return profile

  return {
    username: fallbackParticipantUsername(otherId),
    avatar_url: null,
  }
}
